"use client";
import React from 'react';
import { FleetThemeContext } from './ThemeContext';
import 'mapbox-gl/dist/mapbox-gl.css';
import './fleet.css';
import { Sparkles, Sun, Moon, Search, Bell, X } from 'lucide-react';
import Image from 'next/image';
import strixLogo from '../../fleet-marketing-website/logoforfleet.png';
import strixDarkLogo from './_components/strixdarkmodelogo.png';
import Ably from 'ably';
import { useRouter, usePathname } from 'next/navigation';
import { useSession } from 'next-auth/react';
import type { ReactNode } from 'react';
import { Inter } from 'next/font/google';
import { canManageBilling } from '@/lib/fleet-access';

const inter = Inter({ subsets: ['latin'] });

// Safe redirect - only allow relative URLs or same-origin
function safeRedirect(url: string): void {
  if (!url) return;
  // Allow relative URLs starting with /
  if (url.startsWith('/')) {
    window.location.href = url;
    return;
  }
  // Allow same-origin URLs
  try {
    const parsed = new URL(url);
    if (parsed.origin === window.location.origin) {
      window.location.href = url;
      return;
    }
    // Allow Stripe URLs for checkout
    if (parsed.hostname === 'checkout.stripe.com') {
      window.location.href = url;
      return;
    }
  } catch {
    // Invalid URL, don't redirect
  }
  console.error('Blocked unsafe redirect to:', url);
}

export default function FleetLayout({ children }: { children: React.ReactNode }) {
  const [mode, setMode] = React.useState<'dark' | 'light'>('dark');
  const router = useRouter();
  const pathname = usePathname() || '';
  const { data: session } = useSession();
  const hideHeaderByPath = pathname.startsWith('/fleet/login') || pathname.startsWith('/fleet/auth');
  
  // Check if user can manage billing (fleet admin only)
  const canManage = session?.user ? canManageBilling(session.user) : false;
  const hasNoHeader = React.useMemo(() => {
    let found = false;
    const scan = (node: ReactNode) => {
      if (found) return;
      React.Children.forEach(node as any, (child: any) => {
        if (found) return;
        if (React.isValidElement(child)) {
          if ((child.type as any)?.noFleetHeader) { found = true; return; }
          if (child.props && child.props.children) scan(child.props.children);
        }
      });
    };
    scan(children);
    return found;
  }, [children]);
  const hideHeader = hideHeaderByPath || hasNoHeader;
  const [notifOpen, setNotifOpen] = React.useState(false);
  const [unread, setUnread] = React.useState<number>(0);
  const [items, setItems] = React.useState<Array<{ id:string; message:string; link?:string|null; read:boolean; createdAt:string }>>([]);
  const [loadingNotifs, setLoadingNotifs] = React.useState<boolean>(false);
  const [toasts, setToasts] = React.useState<Array<{ id: string; message: string; link?: string }>>([]);
  const [notifPrefs, setNotifPrefs] = React.useState<{ soundEnabled?: boolean; vibrateEnabled?: boolean; quietHoursStart?: string; quietHoursEnd?: string }>({ soundEnabled: true, vibrateEnabled: false, quietHoursStart:'', quietHoursEnd:'' });
  const [sub, setSub] = React.useState<any>(null);
  const [subLoading, setSubLoading] = React.useState<boolean>(true);
  const [showPlanOnboard, setShowPlanOnboard] = React.useState<boolean>(false);
  const [planCycle, setPlanCycle] = React.useState<'monthly'|'yearly'>('yearly');
  const [planSubmitting, setPlanSubmitting] = React.useState<string>('');

  const loadNotifPrefs = React.useCallback(async ()=>{
    try { const r = await fetch('/api/fleet/settings/notifications'); const j = await r.json(); if (r.ok && j?.success) setNotifPrefs((p)=> ({ ...p, ...(j.settings || {}) })); } catch (error) { console.error('Failed to load notification preferences:', error); }
  }, []);

  const showToast = (message?: string, link?: string) => {
    const msg = message || 'New notification';
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setToasts((p)=> [...p, { id, message: msg, link }]);
    setTimeout(()=> setToasts((p)=> p.filter(t=> t.id !== id)), 4000);
  };

  const loadNotifications = React.useCallback(async () => {
    setLoadingNotifs(true);
    try {
      const res = await fetch('/api/notifications?limit=8');
      const data = await res.json();
      if (res.ok && data?.success) {
        setItems(data.notifications || []);
        setUnread(data.unreadCount || 0);
      }
    } catch (error) {
      console.error('Failed to load notifications:', error);
    }
    setLoadingNotifs(false);
  }, []);

  React.useEffect(() => {
    loadNotifications();
    loadNotifPrefs();
    const id = setInterval(loadNotifications, 30000);
    return () => clearInterval(id);
  }, [loadNotifications, loadNotifPrefs]);

  // Global pricing modal trigger
  React.useEffect(() => {
    const open = (e: any) => {
      try {
        const cyc = e?.detail?.cycle;
        if (cyc === 'monthly' || cyc === 'yearly') setPlanCycle(cyc);
      } catch {}
      setShowPlanOnboard(true);
    };
    window.addEventListener('fleet:openPricing', open as any);
    return () => window.removeEventListener('fleet:openPricing', open as any);
  }, []);

  React.useEffect(() => {
    // Only fetch billing status if user is authenticated and can manage billing
    if (!session || !canManage) {
      setSubLoading(false);
      return;
    }
    
    (async () => {
      try { const r = await fetch('/api/fleet/billing/status'); const j = await r.json(); if (r.ok) setSub(j || null); else setSub(null); } catch (error) { console.error('Failed to load billing status:', error); setSub(null); }
      setSubLoading(false);
    })();
  }, [session, canManage]);

  const subscribePlan = async (lookup: string) => {
    try {
      setPlanSubmitting(lookup);
      const r = await fetch('/api/fleet/billing/subscribe', { method:'POST', headers:{ 'Content-Type':'application/json' }, body: JSON.stringify({ planLookupKey: lookup }) });
      const j = await r.json();
      if (!r.ok || !j?.url) throw new Error(j?.error || 'Failed');
      safeRedirect(j.url as string);
    } catch (e:any) { alert(e?.message || 'Failed'); } finally { setPlanSubmitting(''); }
  };

  // Realtime notifications via Ably
  React.useEffect(() => {
    let client: Ably.Realtime | null = null;
    let channel: Ably.Types.RealtimeChannelCallbacks | null = null;
    let fallbackId: any = null;
    const startFallback = () => { if (!fallbackId) fallbackId = setInterval(loadNotifications, 10000); };
    const stopFallback = () => { if (fallbackId) { clearInterval(fallbackId); fallbackId = null; } };
    (async () => {
      try {
        // get current user id from notifications API
        const res = await fetch('/api/notifications?limit=1');
        const data = await res.json();
        const uid = data?.user?.id;
        if (!uid) return;
        client = new Ably.Realtime({ authUrl: '/api/ably/token' } as any);
        client.connection.on((stateChange: any) => {
          const s = stateChange?.current || '';
          if (s === 'connected') stopFallback();
          if (s === 'disconnected' || s === 'failed' || s === 'suspended') startFallback();
        });
        channel = client.channels.get(`notifications:${uid}`);
        const handler = (msg: any) => {
          try {
            const data = msg?.data || {};
            if (msg?.name === 'created') {
              // Respect sound/vibrate & quiet hours
              const toMins = (s: string) => { const [h,m] = (s||'').split(':').map(Number); return (isNaN(h)||isNaN(m))? null : (h*60+m); };
              const sM = toMins(notifPrefs.quietHoursStart || '');
              const eM = toMins(notifPrefs.quietHoursEnd || '');
              let quiet = false; if (sM!=null && eM!=null) { const now = new Date(); const nM = now.getHours()*60 + now.getMinutes(); quiet = sM<=eM ? (nM>=sM && nM<eM) : (nM>=sM || nM<eM); }
              if (!quiet && notifPrefs.soundEnabled) {
                try { const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext; if (Ctx) { const ctx = new Ctx(); const o = ctx.createOscillator(); const g = ctx.createGain(); o.type='sine'; o.frequency.value=880; g.gain.value=0.05; o.connect(g); g.connect(ctx.destination); o.start(); setTimeout(()=>{ try{o.stop(); ctx.close();}catch{} }, 140);} } catch {}
              }
              if (!quiet && notifPrefs.vibrateEnabled && (navigator as any).vibrate) { try { (navigator as any).vibrate(60); } catch {} }
              showToast(data?.message, data?.link);
            }
          } catch {}
          loadNotifications();
        };
        channel.subscribe('created', handler);
        channel.subscribe('updated', handler);
      } catch (e) { console.error('Ably subscribe error (notifications)', e); }
    })();
    return () => { try { stopFallback(); channel?.unsubscribe(); client?.close(); } catch {} };
  }, [loadNotifications]);

  const markAllRead = async () => {
    try {
      const res = await fetch('/api/notifications', { method:'PATCH', headers:{ 'Content-Type':'application/json' }, body: JSON.stringify({ markAll: true }) });
      const data = await res.json();
      if (res.ok && data?.success) {
        setUnread(data.unreadCount || 0);
        setItems((prev)=> prev.map(n => ({ ...n, read: true })));
      }
    } catch (error) { console.error('Failed to mark notifications as read:', error); }
  };

  const openNotification = async (n: { id:string; link?:string|null; read:boolean }) => {
    try {
      if (!n.read) {
        await fetch('/api/notifications', { method:'PATCH', headers:{ 'Content-Type':'application/json' }, body: JSON.stringify({ notificationId: n.id }) });
        setItems((prev)=> prev.map(x => x.id===n.id ? { ...x, read:true } : x));
        setUnread((u)=> Math.max(0, u-1));
      }
    } catch (error) { console.error('Failed to mark notification as read:', error); }
    if (n.link) router.push(n.link);
    setNotifOpen(false);
  };
  return (
    <FleetThemeContext.Provider value={mode}>
    <div className={`${inter.className} min-h-screen w-full fleet-theme ${mode === 'light' ? 'fleet-light' : ''}`} style={{ backgroundColor: 'var(--fleet-bg)', color: 'var(--fleet-text)' }}>
      <header
        className="h-12 flex items-center px-4 gap-3 shadow-sm"
        style={{
          background: 'linear-gradient(180deg, rgba(255,255,255,0.04), rgba(0,0,0,0))',
          borderBottom: '1px solid var(--fleet-border)',
          display: hideHeader ? 'none' : undefined,
        }}
        aria-hidden={hideHeader ? true : undefined}
      >
        <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--fleet-text)' }}>
          <Image src={mode === 'dark' ? strixDarkLogo : strixLogo} alt="Smart Strix" className="h-7 w-auto" priority />
        </div>
        <FleetGlobalSearch />
        <div className="ml-auto flex items-center gap-2 relative">
          <button aria-label="Notifications" className="relative h-8 w-8 rounded-md flex items-center justify-center" style={{ background: 'var(--fleet-subtle)', border: '1px solid var(--fleet-border)' }} onClick={()=> setNotifOpen((o)=>!o)}>
            <Bell size={14} />
            {unread>0 && (
              <span className="absolute -top-1 -right-1 text-[10px] px-1.5 py-0.5 rounded-full" style={{ background:'var(--fleet-accent)', color:'#000', border:'1px solid var(--fleet-border)' }}>{unread>99?'99+':unread}</span>
            )}
          </button>
          {notifOpen && (
            <div className="absolute right-0 top-10 w-80 rounded-lg border overflow-hidden z-50" style={{ borderColor:'var(--fleet-border)', background:'var(--fleet-panel)' }}>
              <div className="px-3 py-2 text-[12px] flex items-center justify-between border-b" style={{ borderColor:'var(--fleet-border)' }}>
                <div className="font-medium">Notifications</div>
                <div className="flex items-center gap-2">
                  <button className="text-[12px] underline" onClick={loadNotifications} disabled={loadingNotifs} style={{ color:'var(--fleet-muted)' }}>{loadingNotifs?'Refreshing…':'Refresh'}</button>
                  <button className="text-[12px] underline" onClick={markAllRead} style={{ color:'var(--fleet-muted)' }}>Mark all read</button>
                </div>
              </div>
              <div className="max-h-80 overflow-auto">
                {items.length===0 && (
                  <div className="px-3 py-6 text-[12px]" style={{ color:'var(--fleet-muted)' }}>No notifications</div>
                )}
                {items.map(n => (
                  <button key={n.id} className="w-full text-left px-3 py-2 border-b last:border-b-0" style={{ borderColor:'var(--fleet-border)', background:n.read?'transparent':'color-mix(in srgb, var(--fleet-indigo) 10%, transparent)' }} onClick={()=>openNotification(n)}>
                    <div className="text-[12px]" style={{ color:'var(--fleet-text)' }}>{n.message}</div>
                    <div className="text-[10px] mt-0.5" style={{ color:'var(--fleet-muted)' }}>{new Date(n.createdAt).toLocaleString()}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
          {(!subLoading && !(sub?.planLookupKey||'').startsWith('advanced_')) && (
            <button
              className="px-2 py-0.5 text-[11px] rounded-full border"
              style={{ background: '#f59e0b', borderColor: 'var(--fleet-border)', color: '#000' }}
              onClick={()=> setShowPlanOnboard(true)}
            >
              Upgrade
            </button>
          )}
          <button className="px-2 py-1 text-[12px] rounded inline-flex items-center gap-1" style={{ backgroundColor: 'var(--fleet-subtle)', border: '1px solid var(--fleet-border)', color: 'var(--fleet-text)' }} onClick={() => setMode((m) => (m === 'dark' ? 'light' : 'dark'))}>
            {mode === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
            {mode === 'dark' ? 'Light' : 'Dark'}
          </button>
        </div>
      </header>
      {/* Pricing modal from header */}
      {!hideHeader && showPlanOnboard && (
        <div className="fixed inset-0 z-[60] bg-black/60" onClick={()=> setShowPlanOnboard(false)}>
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[960px] max-w-[95vw]" onClick={(e)=> e.stopPropagation()}>
            <div className="rounded-lg overflow-hidden relative" style={{ background:'var(--fleet-panel)', border:'1px solid var(--fleet-border)', color:'var(--fleet-text)' }}>
              <button aria-label="Close" className="absolute right-2 top-2 h-7 w-7 rounded flex items-center justify-center" style={{ background:'var(--fleet-subtle)', border:'1px solid var(--fleet-border)' }} onClick={()=> setShowPlanOnboard(false)}>
                <X size={14} />
              </button>
              <div className="p-4 border-b flex items-center justify-between" style={{ borderColor:'var(--fleet-border)' }}>
                <div>
                  <div className="text-[12px]" style={{ color:'var(--fleet-muted)' }}>Choose a plan</div>
                  <div className="text-[16px] font-semibold">Upgrade your Strix plan</div>
                </div>
                <div className="inline-flex items-center gap-1 text-[11px] rounded-full border p-0.5" style={{ borderColor:'var(--fleet-border)', background:'var(--fleet-subtle)' }}>
                  <button onClick={()=> setPlanCycle('monthly')} className={`px-3 py-1 rounded-full transition-colors`} style={{ background: planCycle==='monthly' ? 'var(--fleet-accent)' : 'transparent', color: planCycle==='monthly' ? '#000' : 'var(--fleet-muted)' }}>
                    {planCycle==='monthly' ? 'Monthly ✓' : 'Monthly'}
                  </button>
                  <button onClick={()=> setPlanCycle('yearly')} className={`px-3 py-1 rounded-full transition-colors`} style={{ background: planCycle==='yearly' ? 'var(--fleet-accent)' : 'transparent', color: planCycle==='yearly' ? '#000' : 'var(--fleet-muted)' }}>
                    {planCycle==='yearly' ? 'Yearly ✓' : 'Yearly'}
                  </button>
                </div>
              </div>
              <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                {(!(sub?.planLookupKey||'').startsWith('starter_')) && (
                  <div className="rounded border p-3 relative overflow-hidden" style={{ borderColor:'var(--fleet-border)', background:'linear-gradient(180deg, rgba(255,255,255,0.02), rgba(0,0,0,0))', boxShadow:'0 10px 30px rgba(0,0,0,0.15)' }}>
                    <div className="text-[13px] font-semibold">Strix Starter</div>
                    <div className="text-[11px] mt-1" style={{ color:'var(--fleet-muted)' }}>Up to 3 drivers • Manual assignment • Team analytics</div>
                    {planCycle==='yearly' && (
                      <div className="mt-2 inline-flex items-center gap-2 text-[10px] px-2 py-0.5 rounded-full" style={{ background:'rgba(34,197,94,0.12)', border:'1px solid rgba(34,197,94,0.4)', color:'#22c55e' }}>Save 15% (2 months free)</div>
                    )}
                    <div className="mt-3 flex items-center gap-2">
                      <div className="text-[20px] font-semibold">{planCycle==='monthly' ? '£49.99/mo' : '£509/yr'}</div>
                    </div>
                    <div className="mt-3 space-y-1 text-[11px]" style={{ color:'var(--fleet-text)' }}>
                      <div className="font-medium" style={{ color:'var(--fleet-muted)' }}>Team Management</div>
                      <div className="mt-1 space-y-1">
                        {['Add up to 3 drivers','Control job visibility','Manage distribution'].map((t,i)=> (
                          <div key={`hd-st-${i}`} className="flex items-start gap-2"><svg className="h-3 w-3 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" style={{ color:'#22c55e' }}><path d="M20 6L9 17l-5-5"/></svg><span>{t}</span></div>
                        ))}
                      </div>
                    </div>
                    <div className="mt-3 flex gap-2">
                      <button className="px-3 py-1 text-[12px] rounded w-full md:w-auto" style={{ background:'var(--fleet-accent)', color:'#000' }} disabled={planSubmitting=== (planCycle==='monthly'?'starter_monthly':'starter_yearly')} onClick={()=> subscribePlan(planCycle==='monthly'?'starter_monthly':'starter_yearly')}>{planSubmitting=== (planCycle==='monthly'?'starter_monthly':'starter_yearly') ? 'Loading…' : (planCycle==='monthly' ? 'Choose Starter (Monthly)' : 'Choose Starter (Yearly)')}</button>
                    </div>
                  </div>
                )}
                <div className="relative rounded border p-3 overflow-hidden" style={{ borderColor:'var(--fleet-border)', background:'linear-gradient(180deg, rgba(99,102,241,0.08), rgba(0,0,0,0))', boxShadow:'0 14px 36px rgba(99,102,241,0.18)' }}>
                  <div className="absolute right-3 top-3 text-[10px] px-2 py-0.5 rounded-full" style={{ background:'rgba(99,102,241,0.16)', border:'1px solid rgba(99,102,241,0.4)', color:'#a5b4fc' }}>Most popular</div>
                  <div className="text-[13px] font-semibold">Strix Advanced</div>
                  <div className="text-[11px] mt-1" style={{ color:'var(--fleet-muted)' }}>Unlimited drivers • Everything in Starter • Live GPS</div>
                  {planCycle==='yearly' && (
                    <div className="mt-2 inline-flex items-center gap-2 text-[10px] px-2 py-0.5 rounded-full" style={{ background:'rgba(34,197,94,0.12)', border:'1px solid rgba(34,197,94,0.4)', color:'#22c55e' }}>Save 25% (3 months free)</div>
                  )}
                  <div className="mt-3 flex items-center gap-2">
                    <div className="text-[20px] font-semibold">{planCycle==='monthly' ? '£99/mo' : '£891/yr'}</div>
                  </div>
                  <div className="mt-3 space-y-1 text-[11px]" style={{ color:'var(--fleet-text)' }}>
                    <div className="font-medium" style={{ color:'var(--fleet-muted)' }}>Live GPS Tracking (Advanced Only)</div>
                    <div className="mt-1 space-y-1">
                      {['Real-time GPS on active jobs','Map visibility and coordination'].map((t,i)=> (
                        <div key={`hd-ad-${i}`} className="flex items-start gap-2"><svg className="h-3 w-3 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" style={{ color:'#22c55e' }}><path d="M20 6L9 17l-5-5"/></svg><span>{t}</span></div>
                      ))}
                    </div>
                  </div>
                  <div className="mt-3 flex gap-2">
                    <button className="px-3 py-1 text-[12px] rounded w-full md:w-auto" style={{ background:'var(--fleet-accent)', color:'#000' }} disabled={planSubmitting=== (planCycle==='monthly'?'advanced_monthly':'advanced_yearly')} onClick={()=> subscribePlan(planCycle==='monthly'?'advanced_monthly':'advanced_yearly')}>{planSubmitting=== (planCycle==='monthly'?'advanced_monthly':'advanced_yearly') ? 'Loading…' : (planCycle==='monthly' ? 'Choose Advanced (Monthly)' : 'Choose Advanced (Yearly)')}</button>
                  </div>
                </div>
              </div>
              <div className="p-4 border-t flex items-center justify-between" style={{ borderColor:'var(--fleet-border)' }}>
                <div className="text-[11px]" style={{ color:'var(--fleet-muted)' }}>You can change or cancel anytime in Manage plan.</div>
                <div className="flex items-center gap-2">
                  {(!((sub?.planLookupKey||'').startsWith('starter_') || (sub?.planLookupKey||'').startsWith('advanced_'))) && planCycle==='monthly' && (
                    <button className="px-3 py-1 text-[12px] rounded border" style={{ border:'1px solid var(--fleet-border)', color:'var(--fleet-text)' }} onClick={()=> setShowPlanOnboard(false)}>Continue as Free</button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Toasts */}
      {toasts.length>0 && (
        <div className="fixed bottom-4 right-4 space-y-2 z-[60]">
          {toasts.map(t => (
            <button key={t.id} className="rounded border px-3 py-2 text-[12px] shadow-sm max-w-xs text-left"
              onClick={()=> { if (t.link) router.push(t.link); setToasts((p)=> p.filter(x=>x.id!==t.id)); }}
              style={{ borderColor:'var(--fleet-border)', background:'var(--fleet-panel)', color:'var(--fleet-text)' }}>
              {t.message}
            </button>
          ))}
        </div>
      )}
      {children}
    </div>
    </FleetThemeContext.Provider>
  );
}


function FleetGlobalSearch() {
  const router = useRouter();
  const [q, setQ] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [res, setRes] = React.useState<{ jobs:any[]; drivers:any[]; vehicles:any[] }>({ jobs:[], drivers:[], vehicles:[] });
  const boxRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    const onDoc = (e: MouseEvent) => { if (!boxRef.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  React.useEffect(() => {
    const id = setTimeout(async () => {
      if (!q.trim()) { setRes({ jobs:[], drivers:[], vehicles:[] }); return; }
      setLoading(true);
      try {
        const r = await fetch(`/api/fleet/search?q=${encodeURIComponent(q.trim())}`);
        const j = await r.json();
        if (r.ok && j?.success) setRes({ jobs: j.jobs||[], drivers: j.drivers||[], vehicles: j.vehicles||[] });
      } catch {}
      setLoading(false);
    }, 250);
    return () => clearTimeout(id);
  }, [q]);
  return (
    <div className="ml-4 flex-1 max-w-lg relative" ref={boxRef}>
      <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 opacity-60" />
      <input
        value={q}
        onChange={(e)=> { setQ(e.target.value); setOpen(true); }}
        onFocus={()=> setOpen(true)}
        placeholder="Search drivers, jobs, vehicles…"
        className="w-full text-[12px] rounded pl-7 pr-2 py-1 focus:outline-none focus:ring-1"
        style={{ backgroundColor: 'var(--fleet-subtle)', border: '1px solid var(--fleet-border)', color: 'var(--fleet-text)' }} />
      {open && (q.trim().length>0) && (
        <div className="absolute left-0 right-0 top-8 rounded-lg border overflow-hidden z-50" style={{ borderColor:'var(--fleet-border)', background:'var(--fleet-panel)' }}>
          <div className="p-2 text-[11px]" style={{ color:'var(--fleet-muted)' }}>{loading? 'Searching…' : 'Results'}</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 p-2">
            <div>
              <div className="text-[11px] mb-1" style={{ color:'var(--fleet-muted)' }}>Jobs</div>
              {res.jobs.length===0 && <div className="text-[12px]" style={{ color:'var(--fleet-muted)' }}>No jobs</div>}
              {res.jobs.map(j => (
                <button key={j.id} onClick={()=> { router.push(`/fleet/jobs/${j.id}`); setOpen(false); }} className="w-full text-left px-2 py-1 rounded border mb-1"
                  style={{ borderColor:'var(--fleet-border)' }}>
                  <div className="text-[12px]">{j.type} · {j.id.slice(0,6)}</div>
                  <div className="text-[11px]" style={{ color:'var(--fleet-muted)' }}>{j.fromAddress?.formatted_address || ''} → {j.toAddress?.formatted_address || ''}</div>
                </button>
              ))}
            </div>
            <div>
              <div className="text-[11px] mb-1" style={{ color:'var(--fleet-muted)' }}>Drivers</div>
              {res.drivers.length===0 && <div className="text-[12px]" style={{ color:'var(--fleet-muted)' }}>No drivers</div>}
              {res.drivers.map(d => (
                <button key={d.id} onClick={()=> { router.push(`/fleet/drivers/${d.id}`); setOpen(false); }} className="w-full text-left px-2 py-1 rounded border mb-1"
                  style={{ borderColor:'var(--fleet-border)' }}>
                  <div className="text-[12px]">{d.name || d.email}</div>
                  <div className="text-[11px]" style={{ color:'var(--fleet-muted)' }}>{d.email}</div>
                </button>
              ))}
            </div>
            <div>
              <div className="text-[11px] mb-1" style={{ color:'var(--fleet-muted)' }}>Vehicles</div>
              {res.vehicles.length===0 && <div className="text-[12px]" style={{ color:'var(--fleet-muted)' }}>No vehicles</div>}
              {res.vehicles.map(v => (
                <button key={v.id} onClick={()=> { router.push(`/fleet/vehicles/${v.id}`); setOpen(false); }} className="w-full text-left px-2 py-1 rounded border mb-1"
                  style={{ borderColor:'var(--fleet-border)' }}>
                  <div className="text-[12px]">{v.reg || `${v.make} ${v.model}`}</div>
                  <div className="text-[11px]" style={{ color:'var(--fleet-muted)' }}>{v.make} {v.model}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


